import {
  type ExternalLoginProviders,
  type PasswordRequirementsModel,
  templates,
  TypesafeApi,
  type Endpoint,
} from '@nosinovacao/nosid-mfe-common';
import { env } from './env';
import { auth } from './lib';

export interface UsernameAlias {
  Username: string;
  Type: 'Other' | 'Mobile' | 'Email';
}

interface User {
  Id: string;
  DisplayName: string;
  BirthDate: string;
  Nif: string;
  PamPictureUri: string | null;
  Gender: 'M' | 'F';
  State: 'Pending' | 'Active';
  UsernameAliases: UsernameAlias[];
}

interface SecondaryUser {
  DisplayName: string;
  Email: string;
  Applications: string[];
}

interface ClientApplication {
  Name: string;
  WebPageUrl: string;
  Type: 'Android' | 'iOS' | 'Web';
  Id: string;
}

export interface Application {
  Id: string;
  Name: string;
  Description: string;
  WebPageUrl: string;
  LogoUrl: string | null;
  Clients: ClientApplication[];
  IsAuthorized: boolean;
  CanRequestAccess: boolean;
  AdminUsersOnly: boolean;
}

export interface Device {
  Id: string;
  Name: string;
  FirstAccess: string;
  LastAccess: string;
  LockExpireDate: string;
  IsLocked: boolean;
  City: string;
  Country: string;
  Type:
    | 'IOSSMARTPHONE'
    | 'IOSTABLET'
    | 'ANDROIDSMARTPHONE'
    | 'ANDROIDTABLET'
    | 'PC'
    | 'MAC'
    | 'APPLETV'
    | 'ANDROIDTV'
    | 'XBOX'
    | 'PS4';
}

interface SendSecurityCode {
  TargetId: string;
  Type: 'Sms' | 'Email';
}
interface RequestStatus {
  Success: boolean;
}
export interface SecurityCode extends SendSecurityCode {
  Code: string;
}

interface ValidateCodeComplete {
  Value: boolean;
}

interface PasswordValidation {
  Password: string;
}

export interface UpdatePassword {
  NewPassword: string;
  OldPassword?: string;
  SecurityCode?: SecurityCode;
}

export interface MfaInfo {
  Enabled: boolean;
  EnabledAt: string | null;
  Device: string;
  // ? null when the user manager was unreachable, so it stays distinct from 0 codes left
  RecoveryCodesRemaining: number | null;
}

export interface MfaSetup {
  Secret: string;
  Uri: string;
}

export interface MfaRecoveryCodes {
  RecoveryCodes: string[];
}

export interface PersonalInfo {
  DisplayName: string;
  BirthDate: string | null | undefined;
  Nif: string;
  Gender: User['Gender'];
  PamPictureUri: string | null;
  Type: 'Guest' | 'Primary' | 'Secondary';
  HasTvService: boolean;
  UsernameAliases: UsernameAlias[];
  TwoFactorAuthEnabled: boolean;
  Mfa: MfaInfo;
  DefaultAccountId: string;
  CanHaveSecondaryUsers: boolean;
}

interface PersonalInfoPhotoUpdated {
  Id: string;
  Url: string;
}

type AccountActivityQuery = {
  Top: number;
  Skip: number;
  Order: string;
  Activities: string[];
  ClientAppIds: string[];
  Devices: string[];
  Locations: string[];
};

export interface AccountActivityFilters {
  Top?: number;
  Skip?: number;
  Activities: string[];
  Devices: string[];
  Locations: string[];
  Applications: string[];
  Orders: string[];
}

export interface AccountActivityItem {
  ActivityType: string;
  ActivityDate: string;
  UserId: string;
  UserAgent: string;
  ClientLocation: {
    IpAddress: string;
    Longitude: number;
    Latitude: number;
    PostalCode: string;
    City: string;
    Region: string;
    Country: string;
    CountryCode: string;
  };
  RegisterProcess: string;
  ClientAppId: string;
  DeviceModel: string;
  Os: string;
  Browser: string;
  Guid: string;
}

export interface AccountActivity {
  Count: number;
  AvailableFilters: AccountActivityFilters;
  Items: AccountActivityItem[];
  ActiveFilters: AccountActivityFilters;
}

export interface Avatar {
  Id: string;
  ImageId: string;
  ImageUri?: string;
}
export interface Profile {
  ProfileId?: string;
  Type: 'NextGen' | 'Kids';
  DisplayName?: string;
  Nickname: string;
  Pin?: string;
  ContentRatingLimit: string;
  Avatar?: Avatar;
  ServiceAccount?: string;
}

interface AppClient {
  Id: string;
  Name: string;
  WebPageUrl: string;
  Type: 'Android' | 'iOS' | 'Web';
}

export interface PrivacyFlag {
  Title: string;
  Message: string;
  NotAcceptedText: string;
  FlagOrder: number;
  IsMandatory: boolean;
  IsAccepted: boolean;
  Type: string;
  TextCategory: string;
}

export interface AppInfoWithPrivacyFlags {
  PrivacyFlags: PrivacyFlag[];
  Client: AppClient;
  Id: string;
  Name: string;
  Description: string;
  WebPageUrl: string;
  LogoUrl: string;
  AdminUsersOnly: boolean;
}

export interface NewPrivacyFlag {
  IsAccepted: boolean;
  Type: string;
  TextCategory: string;
}

export interface PrivacyFlagsUpdate {
  ClientId: string;
  PrivacyFlags: NewPrivacyFlag[];
}

interface Service {
  Id: string;
  CustomerId: string;
  Address: string;
  Package: string;
  MobileNumber?: string;
  IsDefault: boolean;
  HasInternetService: boolean;
  HasTvService: boolean;
  HasMobileService: boolean;
  HasMobileNetService: boolean;
  HasVoiceService: boolean;
  IsStandAloneAccount: boolean;
}

interface Holder {
  PrimaryUserDisplayName: string;
  HouseHoldName: string;
  PictureUri: string;
  ServiceAccounts: Service[];
  UsernameAliases: UsernameAlias[];
}

export interface AccountServices {
  Type: PersonalInfo['Type'];
  DefaultAccountId: string;
  Holder: Holder;
}

export interface ServiceAccountPin {
  Type: 'purchase' | 'parental' | 'stb';
  Code: string;
}

interface LoginOptions {
  UsernameAliases: UsernameAlias[];
  SocialNetworks: SocialNetworks[];
}

interface SocialNetworks {
  Provider: ExternalLoginProviders;
}

interface UpdateLoginOptions {
  UsernameAliases: UsernameAlias[];
  SecurityCodes: SecurityCode[];
}

export const associationTypesList = ['Nif', 'Phone', 'Email', 'Passport'] as const;

export type AssociationType = (typeof associationTypesList)[number];

export interface AccountAssociationContact {
  Contact: string;
  Type: AssociationType;
  Id: number;
}

export type PortalApi = {
  '/api/v1/users': {
    get: Endpoint<{ response: User[] }>;
    post: Endpoint<{ response: User; payload: SecondaryUser }>;
    delete: Endpoint<{ response: void }>;
  };
  '/api/v1/users/secondary/:userId': {
    delete: Endpoint<{ response: void }>;
  };
  '/api/v1/users/secondary/:userId/resend-invite': {
    post: Endpoint<{ response: void }>;
  };
  '/api/v1/applications/:secondaryUserId': {
    get: Endpoint<{ response: Application[] }>;
  };
  '/api/v1/applications/permission/:appId/user/:userId': {
    post: Endpoint<{ response: void; search: { isEnabled: boolean } }>;
  };
  '/api/v1/device-management/:accountId/devices': {
    get: Endpoint<{ response: Device[] }>;
  };
  '/api/v1/device-management/:accountId/devices/:deviceId': {
    delete: Endpoint<{ response: void }>;
  };
  '/api/v1/security/send-code': {
    post: Endpoint<{ response: RequestStatus; payload: SendSecurityCode; captcha: true }>;
  };
  '/api/v1/security/validate-code': {
    post: Endpoint<{ payload: SecurityCode; response: ValidateCodeComplete }>;
  };
  '/api/v1/security/enable-2factor-auth': {
    post: Endpoint<{ response: boolean; payload: SecurityCode }>;
  };
  '/api/v1/security/disable-2factor-auth': {
    post: Endpoint<{ response: boolean; payload: void }>;
  };
  '/api/v1/security/validate-password': {
    post: Endpoint<{
      payload: PasswordValidation;
      response: void;
    }>;
  };
  '/api/v1/security/password-requirements': {
    get: Endpoint<{ response: PasswordRequirementsModel }>;
  };
  '/api/v1/security/new-password': {
    post: Endpoint<{
      response: void;
      payload: UpdatePassword;
    }>;
  };
  '/api/v1/security/invalidate-sessions': {
    put: Endpoint<{ response: void; payload: void }>;
  };
  '/api/v1/mfa': {
    delete: Endpoint<{ response: void }>;
  };
  '/api/v1/mfa/setup': {
    post: Endpoint<{ response: MfaSetup; payload: void }>;
  };
  '/api/v1/mfa/activate': {
    post: Endpoint<{ payload: { Code: string }; response: MfaRecoveryCodes }>;
  };
  '/api/v1/applications': {
    get: Endpoint<{ response: Application[] }>;
  };
  '/api/v1/applications/client-names': {
    get: Endpoint<{ response: Record<string, string> }>;
  };
  '/api/v1/applications/request-access/:id': {
    post: Endpoint<{ response: true }>;
  };
  '/api/v1/personal-info': {
    get: Endpoint<{ response: PersonalInfo }>;
    put: Endpoint<{ response: PersonalInfo; payload: PersonalInfo }>;
  };
  '/api/v1/personal-info/profile-picture': {
    post: Endpoint<{ response: PersonalInfoPhotoUpdated; payload: FormData }>;
  };
  '/api/v1/personal-info/activity-logs': {
    get: Endpoint<{
      response: AccountActivity;
      search: AccountActivityQuery;
    }>;
  };
  '/api/v1/profiles/avatars': {
    get: Endpoint<{ response: Avatar[]; search: { type: 'Preset' | 'Kids' | 'Woo' } }>;
  };
  '/api/v1/profiles/:sa': {
    get: Endpoint<{ response: Profile[] }>;
  };
  'api/v1/profiles': {
    post: Endpoint<{ response: Profile; payload: Profile }>;
    put: Endpoint<{ response: Profile; payload: Profile }>;
  };
  'api/v2/profiles': {
    put: Endpoint<{
      response: Profile;
      payload: {
        Profile: Profile;
        SecurityCode?: SecurityCode;
        ProfilePin?: string;
      };
    }>;
  };
  '/api/v1/profiles/:sa/:profileId': {
    delete: Endpoint<{ response: void }>;
  };
  '/api/v1/privacy-policies': {
    get: Endpoint<{ response: AppInfoWithPrivacyFlags[] }>;
    put: Endpoint<{ response: void; payload: PrivacyFlagsUpdate }>;
  };
  '/api/v1/services': {
    get: Endpoint<{ response: AccountServices }>;
  };
  '/api/v1/account-association/default-account': {
    post: Endpoint<{ response: void; payload: { ServiceAccount: string } }>;
  };
  '/api/v1/services/pins/:serviceId': {
    get: Endpoint<{ response: ServiceAccountPin[] }>;
    post: Endpoint<{ response: ServiceAccountPin[]; payload: ServiceAccountPin[] }>;
  };
  '/api/v2/services/pins/:serviceId': {
    post: Endpoint<{
      response: ServiceAccountPin[];
      payload: { SecurityCode: SecurityCode; Pins: ServiceAccountPin[] };
    }>;
  };
  '/api/v1/personal-info/login-options': {
    get: Endpoint<{ response: LoginOptions }>;
  };
  '/api/v1/personal-info/username-aliases': {
    put: Endpoint<{ payload: UpdateLoginOptions; response: UsernameAlias[] }>;
  };
  '/api/v1/personal-info/username-alias': {
    delete: Endpoint<{ response: void; search: { 'x-user-id': string } }>;
  };
  '/api/v1/personal-info/validate-aliases': {
    post: Endpoint<{ response: Record<string, boolean>; payload: UsernameAlias[] }>;
  };
  '/api/v1/account-association': {
    get: Endpoint<{
      response: AccountAssociationContact[];
      search: { Contact: string; Type: string };
    }>;
    post: Endpoint<{
      response: AccountServices;
      payload: {
        TargetId: string;
        IdentificationType: AccountAssociationContact['Type'];
        ContactId: number | undefined;
        Code: string;
      };
      search: { associationConfirmed: string };
    }>;
  };
  '/api/v1/account-association/is-contact-valid': {
    get: Endpoint<{
      response: void;
      search: { Contact: string; Type: AccountAssociationContact['Type'] };
    }>;
  };
  '/api/v1/account-association/send-code-personal-info': {
    post: Endpoint<{
      response: RequestStatus;
      payload: {
        TargetId: string;
        IdentificationType: AccountAssociationContact['Type'];
        ContactId: number | undefined;
      };
      captcha: true;
    }>;
  };
  '/api/v1/account-association/validate-code-personal-info': {
    post: Endpoint<{
      response: ValidateCodeComplete;
      payload: {
        TargetId: string;
        IdentificationType: AccountAssociationContact['Type'];
        ContactId: number | undefined;
        Code: string;
      };
    }>;
  };
  '/api/v1/social-networks': {
    post: Endpoint<{
      response: void;
      headers: { 'Social-Network-Authorization': string };
    }>;
    delete: Endpoint<{
      response: void;
      search: { authProvider: ExternalLoginProviders };
    }>;
  };
};

const globalForPortalApi = globalThis as unknown as {
  portalApiInstance: TypesafeApi<PortalApi>;
};

export function initApi() {
  globalForPortalApi.portalApiInstance = new TypesafeApi<PortalApi>({
    base: env.PortalApi,
    enterpriseContext: templates.clientTemplate.EnterpriseContext,
    interceptors: {
      request: async (config) => {
        const token = await auth.getToken();
        config.headers.Authorization = `Bearer ${token ?? ''}`;
      },
    },
  });
  return globalForPortalApi.portalApiInstance;
}

function getApiInstance() {
  return globalForPortalApi.portalApiInstance ?? initApi();
}

export const api = new Proxy(
  {},
  {
    get: (_, prop: string) => {
      const instance = getApiInstance();
      const value = instance[prop as keyof typeof instance];
      if (typeof value === 'function') {
        return value.bind(instance);
      }
      return value;
    },
    set: (_, prop, val) => {
      const instance = getApiInstance() as unknown as Record<string | symbol, unknown>;
      instance[prop] = val;
      return true;
    },
  },
) as TypesafeApi<PortalApi>;
